import {Animatable} from "./Animatable";
import {ContainerExtensions} from "./ContainerExtensions";
import {ColorUtil, IPoint} from "./Geom";
import {Mix} from "./Reflection";
import {Scalable} from "./Scalable";

export class Shape extends Mix(PIXI.Graphics, ContainerExtensions, Scalable, Animatable) {

    // https://gist.github.com/markknol/5c5d48655ebac555a6eec41792acdfb6
    public oval(w: number, h: number, cx: number, cy: number): this {
        const lx = cx - w * .5;
        const rx = cx + w * .5;
        const ty = cy - h * .5;
        const by = cy + h * .5;

        const magic = 0.551915024494;
        const xmagic = magic * w * 0.5;
        const ymagic = h * magic * 0.5;

        this.lineTo(cx, ty)
            .bezierCurveTo(cx + xmagic, ty, rx, cy - ymagic, rx, cy)
            .bezierCurveTo(rx, cy + ymagic, cx + xmagic, by, cx, by)
            .bezierCurveTo(cx - xmagic, by, lx, cy + ymagic, lx, cy)
            .bezierCurveTo(lx, cy - ymagic, cx - xmagic, ty, cx, ty);

        return this;
    }

    public destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.killTweens();
        this.cacheAsBitmap = false;
        super.destroy(options);
    }
}

export class Rect extends Shape {

    public readonly anchor: IPoint = {x: 0, y: 0};

    public readonly fillWidth: number;
    public readonly fillHeight: number;
    public readonly cornerRadius?: number;

    constructor(fillWidth: number,
                fillHeight: number,
                color: string | number = 0,
                alpha: number = 1,
                anchor?: IPoint,
                cornerRadius?: number,
                lineStyle?: ILineStyle) {
        super();

        this.fillWidth = fillWidth;
        this.fillHeight = fillHeight;
        this.cornerRadius = cornerRadius;

        if (anchor) {
            this.anchor.x = anchor.x;
            this.anchor.y = anchor.y;
        }

        this.fill(color, alpha, lineStyle);
    }

    public fill(color: string | number, alpha: number = 1, lineStyle?: ILineStyle) {
        const c = ColorUtil.toNumber(color);
        this.clear();
        this.beginFill(c, alpha);
        if (lineStyle && lineStyle.lineWidth) {
            this.lineStyle(
                lineStyle.lineWidth,
                ColorUtil.toNumber(lineStyle.color),
                lineStyle.alpha,
                lineStyle.alignment,
            );
        }
        if (isNaN(this.cornerRadius!)) {
            this.drawRect(
                -this.anchor.x * this.fillWidth,
                -this.anchor.y * this.fillHeight,
                this.fillWidth,
                this.fillHeight);
        } else {
            this.drawRoundedRect(
                -this.anchor.x * this.fillWidth,
                -this.anchor.y * this.fillHeight,
                this.fillWidth,
                this.fillHeight,
                this.cornerRadius!);
        }
        this.endFill();
    }
}

export class Circle extends Shape {

    public readonly anchor: IPoint = {x: 0.5, y: 0.5};
    public readonly radius: number;

    constructor(radius: number,
                color: string | number = 0,
                alpha: number = 1,
                anchor?: IPoint,
                lineStyle?: ILineStyle) {
        super();

        this.radius = radius;

        if (anchor) {
            this.anchor.x = anchor.x;
            this.anchor.y = anchor.y;
        }

        this.fill(color, alpha, lineStyle);
    }

    public fill(color: string | number, alpha: number = 1, lineStyle?: ILineStyle) {
        const c = ColorUtil.toNumber(color);
        this.clear();
        this.beginFill(c, alpha);
        if (lineStyle && lineStyle.lineWidth) {
            this.lineStyle(
                lineStyle.lineWidth,
                ColorUtil.toNumber(lineStyle.color),
                lineStyle.alpha,
                lineStyle.alignment,
            );
        }
        this.drawCircle(
            (0.5 - this.anchor.x) * this.radius * 2,
            (0.5 - this.anchor.y) * this.radius * 2,
            this.radius);
        this.endFill();
    }
}

export class Poly extends Shape {

    get left(): number {
        return this._left!;
    }

    get center(): number {
        return this._center!;
    }

    get right(): number {
        return this._right!;
    }

    get top(): number {
        return this._top!;
    }

    get middle(): number {
        return this._middle!;
    }

    get bottom(): number {
        return this._bottom!;
    }

    public readonly points: number[];

    private _left?: number;
    private _center?: number;
    private _right?: number;
    private _top?: number;
    private _middle?: number;
    private _bottom?: number;

    constructor(points: (number[]) | (IPoint[]) | (Array<[number, number]>),
                color: string | number = 0,
                alpha: number = 1,
                lineStyle?: ILineStyle) {
        super();

        this.points = typeof points[0] === "number" ? points as number[] :
            (points as []).reduce((a: number[], t: IPoint | [number, number]) => {
                if (Array.isArray(t)) {
                    a.push(t[0]);
                    a.push(t[1]);
                } else {
                    a.push(t.x);
                    a.push(t.y);
                }
                return a;
            }, []);
        this.measure();
        this.fill(color, alpha, lineStyle);
    }

    public measure() {
        this._top = Number.MAX_VALUE;
        this._left = Number.MAX_VALUE;
        this._right = Number.MIN_VALUE;
        this._bottom = Number.MIN_VALUE;
        this._center = 0;
        this._middle = 0;
        const n = this.points.length;
        for (let i = 0; i < n; i++) {
            const t = this.points[i];
            if (i % 2 > 0) {
                if (this._top > t) {
                    this._top = t;
                }
                if (this._bottom < t) {
                    this._bottom = t;
                }
                this._middle += t;
            } else {
                if (this._left > t) {
                    this._left = t;
                }
                if (this._right < t) {
                    this._right = t;
                }
                this._center += t;
            }
        }
        this._center = 2 * this._center / n;
        this._middle = 2 * this._middle / n;
    }

    public fill(color: string | number, alpha: number = 1, lineStyle?: ILineStyle) {
        const c = ColorUtil.toNumber(color);
        this.clear();
        if (lineStyle && lineStyle.lineWidth) {
            this.lineStyle(
                lineStyle.lineWidth,
                ColorUtil.toNumber(lineStyle.color),
                lineStyle.alpha,
                lineStyle.alignment,
            );
        }
        this.beginFill(c, alpha);
        if (this.points && this.points.length > 2) {
            let x = this.points[0] as number;
            let y = this.points[1] as number;
            this.moveTo(x, y);
            for (let i = 2; i < this.points.length; i += 2) {
                x = this.points[i] as number;
                y = this.points[i + 1] as number;
                this.lineTo(x, y);
            }
            x = this.points[0];
            y = this.points[1];
            this.lineTo(x, y);
        }
        this.endFill();
    }
}

export interface ILineStyle {
    lineWidth: number;
    color: string | number;
    alpha?: number;
    alignment?: number;
}
